// ACISurface — generic ACI-suggestions renderer (CHG-1274).
//
// Reusable across 4 of the 5 CHG-1250 surfaces (campaign-suggested-actions,
// donor-aci-notes, recurring-intent-provenance, pledge-schedule-rationale).
// The 5th surface (overview-suggestions) keeps its existing AICallout
// renderer + only swaps the data source via useACISuggestions in
// pages/Overview.tsx — AICallout itself is UNCHANGED.
//
// Per CHG-1250 §7.2 response contract: 1-N AISuggestion-shaped cards
// rendered top-to-bottom; each card has a kind chip + title + body +
// optional CTA. Mirrors AICallout's KIND_META tone-mapping so all 5
// surfaces feel chromatically coherent. Empty state is friendly +
// data-aci-empty="true" for tests.
//
// Per CHG-1266 safeFetch contract: zero crashes when backend returns 404.
// Loading state shown briefly; empty state is the steady state when ACI
// runtime isn't yet shipped.

import { useNavigate } from "react-router-dom";
import { useACISuggestions } from "./useACISuggestions";
import type { ACISuggestion, ACISuggestionKind, ACISurfaceId } from "../../api/aci";

const KIND_META: Record<string, { label: string; tone: string }> = {
  campaign_opportunity: { label: "Campaign opportunity", tone: "g-events" },
  at_risk_donor: { label: "At-risk donor", tone: "g-people" },
  seasonal_template: { label: "Seasonal template", tone: "g-communications" },
  kb_entry: { label: "Reference", tone: "g-imports" },
  intent_provenance: { label: "Intent provenance", tone: "g-giving" },
  schedule_rationale: { label: "Schedule rationale", tone: "g-giving" },
};

function kindMeta(kind: ACISuggestionKind) {
  return (
    KIND_META[kind] ?? {
      label: kind.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      tone: "g-home",
    }
  );
}

export interface ACISurfaceProps {
  surface_id: ACISurfaceId;
  /** Required for surfaces 2-5; optional for overview-suggestions. */
  entity_id?: string;
  /** Default 5; clamped to 10 by the backend. */
  limit?: number;
  /** Title for the empty / surfacing wrapper. Defaults match the surface. */
  title?: string;
  /** Compact mode — single-card render (no list). For dense detail-tab usage. */
  compact?: boolean;
}

const DEFAULT_TITLES: Record<ACISurfaceId, string> = {
  "overview-suggestions": "Suggestions",
  "campaign-suggested-actions": "Suggested actions",
  "donor-aci-notes": "Cultural context",
  "recurring-intent-provenance": "Intent context",
  "pledge-schedule-rationale": "Schedule rationale",
};

export function ACISurface({
  surface_id,
  entity_id,
  limit,
  title,
  compact,
}: ACISurfaceProps) {
  const navigate = useNavigate();
  const { suggestions, isLoading, error } = useACISuggestions({ surface_id, entity_id, limit });
  const displayTitle = title ?? DEFAULT_TITLES[surface_id];
  const items = compact ? suggestions.slice(0, 1) : suggestions;

  if (isLoading) {
    return (
      <section
        data-aci-surface={surface_id}
        data-aci-state="loading"
        style={{ padding: 12, fontSize: 12, color: "var(--color-text-muted)" }}
      >
        Loading {displayTitle.toLowerCase()}…
      </section>
    );
  }

  // CHG-1322 v14 — error branch. Pre-v14 ACISurface silently rendered the
  // empty state on any error (safeFetch swallowed everything). Now 5xx /
  // network failures surface as a friendly "unavailable" notice so
  // operators can distinguish "no matches for your org" from "backend down".
  if (error) {
    return (
      <section
        data-aci-surface={surface_id}
        data-aci-state="error"
        data-aci-error="true"
        style={{
          padding: 12,
          fontSize: 12,
          color: "var(--color-text-muted)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: "var(--radius-md)",
          background: "var(--color-surface)",
        }}
      >
        <strong style={{ display: "block", marginBottom: 4, color: "var(--color-text-secondary)" }}>
          {displayTitle}
        </strong>
        Suggestions unavailable.
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section
        data-aci-surface={surface_id}
        data-aci-state="empty"
        data-aci-empty="true"
        style={{
          padding: 12,
          fontSize: 12,
          color: "var(--color-text-muted)",
          border: "1px dashed var(--color-border-subtle)",
          borderRadius: "var(--radius-md)",
        }}
      >
        <strong style={{ display: "block", marginBottom: 4, color: "var(--color-text-secondary)" }}>
          {displayTitle}
        </strong>
        No suggestions yet.
      </section>
    );
  }

  return (
    <section
      data-aci-surface={surface_id}
      data-aci-state="populated"
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      <h3
        style={{
          fontSize: 12,
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight: 600,
          margin: 0,
        }}
      >
        {displayTitle}
      </h3>
      {items.map((s) => (
        <ACICard key={s.id} suggestion={s} onNavigate={navigate} />
      ))}
    </section>
  );
}

interface ACICardProps {
  suggestion: ACISuggestion;
  onNavigate: (route: string) => void;
}

function ACICard({ suggestion, onNavigate }: ACICardProps) {
  const meta = kindMeta(suggestion.kind);
  const onCta = () => {
    if (suggestion.cta?.route) onNavigate(suggestion.cta.route);
  };
  return (
    <article
      data-aci-card-id={suggestion.id}
      data-aci-kind={suggestion.kind}
      style={{
        padding: 12,
        background: "var(--color-surface)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: "var(--radius-md)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <span
        data-aci-kind-chip
        style={{
          alignSelf: "flex-start",
          fontSize: 11,
          fontWeight: 500,
          padding: "2px 8px",
          borderRadius: 9999,
          color: `var(--color-${meta.tone}-from)`,
          border: `1px solid var(--color-${meta.tone}-from)`,
        }}
      >
        {meta.label}
      </span>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text)" }}>{suggestion.title}</div>
      <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{suggestion.body}</div>
      {suggestion.cta ? (
        <button
          type="button"
          onClick={onCta}
          data-aci-cta
          className="btn btn-secondary aci-cta"
          style={{ alignSelf: "flex-start", fontSize: 12, height: 28, padding: "0 10px" }}
        >
          {suggestion.cta.label}
        </button>
      ) : null}
    </article>
  );
}
